/**
 * PropertyOptionService
 *
 * Manages select and multi-select property options.
 *
 * Features:
 * - CRUD operations for options
 * - Reordering support (drag-and-drop)
 * - Usage tracking for smart autocomplete
 * - Option color and icon metadata
 * - Multi-property option sharing
 *
 * Examples:
 * - Status options: To Do, In Progress, Done
 * - Priority options: Low, Medium, High
 * - Tags with custom colors
 */

import { db } from '../db/database';
import { eventBus } from '../events/EventBus';
import { restSelect, restInsert, restUpdate, restDelete } from '../lib/supabaseRest';
import type {
  PropertyOption,
  CreateOptionRequest,
} from '../types/advanced-properties';

export class PropertyOptionService {
  /**
   * Get all options for a property (sorted by position or usage)
   */
  async getOptions(
    propertyId: string,
    sortBy: 'position' | 'usage' = 'position'
  ): Promise<PropertyOption[]> {
    try {
      const options = await db.propertyOptions
        .where({ propertyId })
        .toArray();

      // Sort by chosen criteria
      if (sortBy === 'usage') {
        return options.sort((a, b) => b.usage_count - a.usage_count);
      } else {
        return options.sort((a, b) => a.position - b.position);
      }
    } catch (error) {
      console.error('[PropertyOptionService] Error getting options:', error);
      return [];
    }
  }

  /**
   * Create a new option
   */
  async createOption(request: CreateOptionRequest): Promise<PropertyOption> {
    try {
      // Check if option with this value already exists
      const existing = await this.findOptionByValue(request.property_id, request.value);
      if (existing) {
        throw new Error(`Option with value "${request.value}" already exists`);
      }

      // Get current max position
      const options = await this.getOptions(request.property_id);
      const maxPosition = options.length > 0
        ? Math.max(...options.map(o => o.position))
        : -1;

      const option: PropertyOption = {
        id: this.generateId(),
        property_id: request.property_id,
        value: request.value,
        label: request.label,
        color: request.color || null,
        icon: request.icon || null,
        position: request.position !== undefined ? request.position : maxPosition + 1,
        usage_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Store in Supabase
      const created = await restInsert<PropertyOption>('property_options', [option]);

      // Store locally
      await db.propertyOptions.add(option);

      // Emit event
      eventBus.emit('option:created', {
        propertyId: option.property_id,
        option: created[0],
      });

      return created[0];
    } catch (error) {
      console.error('[PropertyOptionService] Error creating option:', error);
      throw error;
    }
  }

  /**
   * Update an option
   */
  async updateOption(
    optionId: string,
    updates: Partial<Omit<PropertyOption, 'id' | 'property_id' | 'created_at' | 'usage_count'>>
  ): Promise<PropertyOption> {
    try {
      const updatedData = {
        ...updates,
        updated_at: new Date().toISOString(),
      };

      // Update in Supabase
      await restUpdate(
        'property_options',
        updatedData,
        { eq: { id: optionId } }
      );

      // Update locally
      await db.propertyOptions.update(optionId, updatedData);

      // Emit event
      eventBus.emit('option:updated', { optionId, updates });

      // Fetch updated option
      const updated = await db.propertyOptions.get(optionId);
      if (!updated) {
        throw new Error('Option not found after update');
      }

      return updated;
    } catch (error) {
      console.error('[PropertyOptionService] Error updating option:', error);
      throw error;
    }
  }

  /**
   * Delete an option
   */
  async deleteOption(optionId: string): Promise<void> {
    try {
      const option = await db.propertyOptions.get(optionId);
      if (!option) return;

      // Delete from Supabase
      await restDelete('property_options', { eq: { id: optionId } });

      // Delete locally
      await db.propertyOptions.delete(optionId);

      // Emit event
      eventBus.emit('option:deleted', {
        propertyId: option.property_id,
        optionId,
      });
    } catch (error) {
      console.error('[PropertyOptionService] Error deleting option:', error);
      throw error;
    }
  }

  /**
   * Reorder options
   */
  async reorderOptions(
    propertyId: string,
    orderedOptionIds: string[]
  ): Promise<void> {
    try {
      // Update positions
      for (let i = 0; i < orderedOptionIds.length; i++) {
        const optionId = orderedOptionIds[i];

        // Update in Supabase
        await restUpdate(
          'property_options',
          { position: i, updated_at: new Date().toISOString() },
          { eq: { id: optionId } }
        );

        // Update locally
        await db.propertyOptions.update(optionId, { position: i });
      }

      // Emit event
      eventBus.emit('option:reordered', {
        propertyId,
        orderedOptionIds,
      });
    } catch (error) {
      console.error('[PropertyOptionService] Error reordering options:', error);
      throw error;
    }
  }

  /**
   * Increment usage count when option is selected
   */
  async incrementUsage(optionId: string): Promise<void> {
    try {
      const option = await db.propertyOptions.get(optionId);
      if (!option) return;

      const newCount = option.usage_count + 1;

      // Update in Supabase
      await restUpdate(
        'property_options',
        {
          usage_count: newCount,
          updated_at: new Date().toISOString(),
        },
        { eq: { id: optionId } }
      );

      // Update locally
      await db.propertyOptions.update(optionId, {
        usage_count: newCount,
      });

      // Emit event for analytics
      eventBus.emit('option:used', {
        optionId,
        propertyId: option.property_id,
        usageCount: newCount,
      });
    } catch (error) {
      console.error('[PropertyOptionService] Error incrementing usage:', error);
      // Don't throw - usage tracking is best-effort
    }
  }

  /**
   * Find option by value
   */
  async findOptionByValue(
    propertyId: string,
    value: string
  ): Promise<PropertyOption | null> {
    try {
      const option = await db.propertyOptions
        .where({ propertyId, value })
        .first();
      return option || null;
    } catch (error) {
      console.error('[PropertyOptionService] Error finding option:', error);
      return null;
    }
  }

  /**
   * Get options sorted by usage (for autocomplete)
   */
  async getOptionsByUsage(propertyId: string): Promise<PropertyOption[]> {
    return this.getOptions(propertyId, 'usage');
  }

  /**
   * Search options by label (for autocomplete)
   */
  async searchOptions(
    propertyId: string,
    query: string
  ): Promise<PropertyOption[]> {
    try {
      const options = await this.getOptions(propertyId, 'usage');

      if (!query) return options;

      const lowerQuery = query.toLowerCase();
      return options.filter(option =>
        option.label.toLowerCase().includes(lowerQuery) ||
        option.value.toLowerCase().includes(lowerQuery)
      );
    } catch (error) {
      console.error('[PropertyOptionService] Error searching options:', error);
      return [];
    }
  }

  /**
   * Bulk create options from array
   */
  async bulkCreateOptions(
    propertyId: string,
    optionData: Array<{ value: string; label: string; color?: string; icon?: string }>
  ): Promise<PropertyOption[]> {
    try {
      const created: PropertyOption[] = [];

      for (let i = 0; i < optionData.length; i++) {
        const data = optionData[i];
        try {
          const option = await this.createOption({
            property_id: propertyId,
            value: data.value,
            label: data.label,
            color: data.color,
            icon: data.icon,
            position: i,
          });
          created.push(option);
        } catch (error) {
          // Skip duplicates
          console.warn(`[PropertyOptionService] Skipping duplicate option: ${data.value}`);
        }
      }

      return created;
    } catch (error) {
      console.error('[PropertyOptionService] Error bulk creating options:', error);
      throw error;
    }
  }

  /**
   * Get option statistics
   */
  async getOptionStats(propertyId: string): Promise<{
    total: number;
    mostUsed: PropertyOption | null;
    leastUsed: PropertyOption | null;
    totalUsage: number;
  }> {
    try {
      const options = await this.getOptions(propertyId);

      if (options.length === 0) {
        return {
          total: 0,
          mostUsed: null,
          leastUsed: null,
          totalUsage: 0,
        };
      }

      const sorted = [...options].sort((a, b) => b.usage_count - a.usage_count);
      const totalUsage = options.reduce((sum, opt) => sum + opt.usage_count, 0);

      return {
        total: options.length,
        mostUsed: sorted[0],
        leastUsed: sorted[sorted.length - 1],
        totalUsage,
      };
    } catch (error) {
      console.error('[PropertyOptionService] Error getting option stats:', error);
      return {
        total: 0,
        mostUsed: null,
        leastUsed: null,
        totalUsage: 0,
      };
    }
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const propertyOptionService = new PropertyOptionService();
